<script setup lang="ts">
import { ref, watch } from 'vue';
import { refAutoReset } from '@vueuse/core';
import { useCookies } from '@vueuse/integrations/useCookies';

import api from '@/api';
import { updateToken } from '@/api/backend/_http';
import PrivacyConsentModal from '@/components/shared/PrivacyConsentModal.vue';
import { AppValues, BROKER_TOKEN } from '@/helpers/const';
import { hasLiveBrokerSession } from '@/helpers/brokerSession';
import { noWhiteSpaceKeys } from '@/helpers/noWhiteSpaceKeys';
import { toast } from '@/helpers/toast';
import { useAuth } from '@/helpers/useAuth';
import { useUserQuoteProcess } from '@/helpers/useUserQuoteProcess';
import { StepStatus, UserQuoteProcessStep } from '@/types';

const auth = useAuth();
const cookies = useCookies();
const { loanApp, state, saveAndGoNext } = useUserQuoteProcess();

const email = ref(loanApp.value.brokerEmail ?? '');
const verificationCode = ref('');

const loading = ref(false);
const showPrivacyModal = ref(false);
const showSendCodeError = ref(false);
const highlightError = refAutoReset(false, 1000);

const sendOtpCode = async () => {
  const result = await auth.sendPublicOtp({ email: email.value, isFromQuote: true });
  const { success, redirectUrl } = result;

  if (!success) {
    showSendCodeError.value = !result.success;
    highlightError.value = true;
    toast({ type: 'danger', title: 'Unable to send code', description: 'Your OTP failed to send' });
    return false;
  }

  if (redirectUrl) {
    redirectingUrl(redirectUrl);
    return false;
  }

  toast({ type: 'success', title: 'Success', description: 'One time password sent successfully' });

  return success;
};

const verifyOtpCode = async () => {
  try {
    loading.value = true;

    const trimmedEmail = email.value.trim();
    const trimmedCode = verificationCode.value.trim();

    if (!trimmedEmail) {
      toast({ type: 'danger', title: 'Email is missing', description: 'Email is missing' });
      return false;
    }

    if (!trimmedCode) {
      toast({ type: 'danger', title: 'OTP is missing', description: 'OTP is missing' });
      return false;
    }

    const result = await auth.verifyPublicOtp(trimmedEmail, trimmedCode, true);
    const { success, redirectUrl, token } = result;

    if (!success) {
      updateToken(null);
      toast({ type: 'danger', title: 'Error', description: 'Could not determine otp.' });
      return false;
    }

    if (redirectUrl) {
      redirectingUrl(redirectUrl);
      return false;
    }

    if (token) {
      cookies.set(BROKER_TOKEN, token, { path: '/' });

      updateToken(token);

      await setBrokerDetails();

      state.value.context.unaccreditedBroker = undefined;
    } else {
      const currentQuoteApp = state.value.context.loanApp;
      state.value.context.loanApp = {
        ...currentQuoteApp,
        userType: 'UnaccreditedBroker',
      };
    }

    toast({ type: 'success', title: 'Success', description: 'Email verified' });

    return success;
  } catch (err) {
    if (err) {
      toast({ type: 'danger', title: 'Unable to verify', description: 'Contact Support' });
    }
    return false;
  } finally {
    loading.value = false;
  }
};

const redirectingUrl = (redirectUrl: string) => {
  try {
    const currentHost = window.location.host;
    const redirectUrlHost = new URL(redirectUrl).host;

    if (currentHost !== redirectUrlHost) {
      window.location.href = redirectUrl;
      return;
    }
  } catch (error) {
    console.error('Invalid redirect URL:', redirectUrl, error);
  }
};

const setBrokerDetails = async () => {
  const {
    data: { broker },
  } = await api.backend.brokers.user.currentUser();

  if (!broker) return;

  const { id, email, firstName, lastName } = broker;
  const brokerReferenceNumber = 'brokerReferenceNumber' in broker ? broker.brokerReferenceNumber ?? '' : '';

  cookies.set('broker-id', id, { path: '/' });

  const currentQuoteApp = state.value.context.loanApp;
  state.value.context.loanApp = {
    ...currentQuoteApp,
    userType: 'Broker',
    brokerId: id,
    brokerEmail: email,
    brokerReferenceNumber,
    brokerFullName: [firstName, lastName].filter(Boolean).join(' '),
  };
};

watch(
  () => loanApp.value.userType,
  async (newUserType) => {
    if (newUserType === 'Crm') {
      saveAndGoNext();
      return;
    }

    const brokerToken = cookies.get(BROKER_TOKEN);
    const completed = state.value.context.status[UserQuoteProcessStep.VerifyIdentity] === StepStatus.Completed;

    if (newUserType === 'Broker' && brokerToken && !completed) {
      updateToken(brokerToken);

      // The cookie can hold an expired token (no max-age, shared across
      // *.bridgit.com.au). Without this check `setBrokerDetails()` rejects with
      // `invalid-jwt-token-error` and the step is left half-initialised; instead the
      // stale session is cleared and the user verifies by email on this step.
      if (!(await hasLiveBrokerSession())) {
        return;
      }

      await setBrokerDetails();

      saveAndGoNext();
      return;
    }
  },
  { immediate: true },
);

const saveAndContinue = async () => {
  if (await verifyOtpCode()) {
    return saveAndGoNext({ brokerEmail: email.value, verificationCode: verificationCode.value });
  }
};
</script>

<template>
  <div>
    <div class="mb-4 pb-2">
      <h2 class="text-2xl font-medium">
        Email
        <BxHighlight>verification</BxHighlight>
      </h2>
    </div>
    <div>
      <div class="form-control flex">
        <BxInput
          v-model="email"
          class="form-control"
          inputmode="email"
          label="Email address"
          label-type="floating"
          @keypress="noWhiteSpaceKeys"
        />
        <BxButton id="verification-send-otp" class="ml-2 sm:ml-4" variant="secondary" @click="sendOtpCode()"
          >Send code</BxButton
        >
      </div>
      <div v-if="showSendCodeError" class="mb-8 mt-2 pr-2 text-rose-600">
        <p :class="{ 'animate-shakeX': highlightError }">
          Please check that email has been entered correctly before resending the OTP code. If you are unable to
          proceed, please get in touch with us on <span class="font-medium">{{ AppValues.CONTACT }}</span
          >.
        </p>
      </div>
      <BxInput
        id="verification-otp-code"
        v-model="verificationCode"
        inputmode="numeric"
        class="form-control"
        label="6 digit verification code"
        label-type="floating"
        maxlength="6"
      />
    </div>
    <div>
      <div v-if="loanApp.actionType === 'updateExisting'" class="mb-8">
        By clicking "Next" you confirm that you have read and agree to {{ AppValues.COMPANY_NAME + `'s` }}
        <a class="link" @click="showPrivacyModal = true"> Privacy Consent and Electronic Authorisation</a> and consent
        to the collection, use, holding and disclosure of my/our personal information as set out in that document, and
        to receiving notices, statements, disclosures and other documents from
        {{ AppValues.COMPANY_NAME }} electronically.
      </div>
      <ol
        class="otp-instruction mb-4 mt-4 rounded-md border-black px-4 py-4 text-sm text-bridgit-navy transition focus:outline-none focus-visible:ring-2 focus-visible:ring-bridgit-royalBlue focus-visible:ring-offset-2"
      >
        <h3>To complete your email verification:</h3>
        <li>Click the "send code" button above.</li>
        <li>You'll receive a 6-digit verification code on your email.</li>
        <li>Enter the code in the provided box above.</li>
        <li>Click "Next" to complete the verification and continue your application.</li>
        <p>
          If you need help with this step please contact us at {{ AppValues.CONTACT }} or
          <a :href="`emailto:${AppValues.EMAIL}`">{{ AppValues.EMAIL }}</a>
        </p>
      </ol>
      <div class="flex gap-4">
        <BxButton id="verification-next" variant="primary" :loading="loading" @click="saveAndContinue()">Next</BxButton>
      </div>
    </div>
    <PrivacyConsentModal v-model="showPrivacyModal" />
  </div>
</template>

<style lang="scss" scoped>
.otp-instruction {
  background: #ebf1f5;

  li {
    list-style-type: decimal;
    margin-left: 2.1rem;
    line-height: 1.7rem;
  }

  a {
    font-weight: 500;
  }
}

.link {
  @apply cursor-pointer text-bridgit-navy underline hover:text-bridgit-vibrantGreen;
}
</style>
